import React, { useMemo, useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Divider,
	Box,
	IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import logo from "../../ressources/logo.png";
import logoBD from "../../ressources/logo_bd.png";
import logoFoerster from "../../ressources/logo_foerstertag.png";
import { isBioTagPlus, isFoersterTag } from "../../config/brandConfig";
import logoPoultry from "../../ressources/logo_poultry.png";
import DashboardIcon from "@mui/icons-material/Dashboard";
import StoreIcon from "@mui/icons-material/Store";
import ArchiveIcon from "@mui/icons-material/Archive";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsIcon from "@mui/icons-material/Settings";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import InventoryIcon from "@mui/icons-material/Inventory";
import InsightsIcon from "@mui/icons-material/Insights";
import TuneIcon from "@mui/icons-material/Tune";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import SidebarSettingsDialog from "../../components/dialog-components/sidebarsettingsdialog/SidebarSettingsDialog";
import { useAccountType } from "../../hooks/useAccountType";

// Styled components using the design system
const MenuWrapper = styled(Box)(({ theme }) => ({
	width: "300px",
	height: "100%",
	background: "var(--background-paper)", // Plain background instead of gradient
	boxShadow: "var(--shadow-md)", // Using medium shadow from design system
	overflowY: "auto", // Enable scrolling
	overflowX: "hidden", // Prevent horizontal scrolling
	"&::-webkit-scrollbar": {
		display: "none", // Hide scrollbar for WebKit browsers
	},
	scrollbarWidth: "none", // Hide scrollbar for Firefox
	msOverflowStyle: "none", // Hide scrollbar for IE/Edge
}));

const MenuContainer = styled(Box)({
	display: "flex",
	flexDirection: "column",
	width: "90%",
	maxWidth: "100%", // Prevent expansion beyond parent
	height: "100%",
	margin: "0 auto",
	justifyContent: "space-between",
	padding: "16px 0", // Using md spacing from design system
	boxSizing: "border-box", // Include padding in width calculation
});

const LogoContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	marginBottom: theme.spacing(3),
	position: "relative",
}));

const SettingsIconButton = styled(IconButton)(({ theme }) => ({
	position: "absolute",
	top: 0,
	right: 0,
	color: "var(--primary-main)",
	padding: theme.spacing(1),
	transition: "all 0.3s ease",
	"&:hover": {
		backgroundColor: "var(--grey-100)",
		transform: "scale(1.1)",
	},
}));

const Logo = styled("img")(({ theme }) => ({
	cursor: "pointer",
	width: "40%",
	margin: "0 auto",
	padding: theme.spacing(2),
	backgroundColor: "var(--primary-main)",
	borderRadius: "var(--radius-sm)", // Using small border radius from design system
	transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)", // Improved animation curve
	"&:hover": {
		transform: "scale(1.1)",
	},
}));

// Add error handling for logo
const LogoWithFallback = ({ src, alt, ...props }) => {
	const [error, setError] = React.useState(false);

	if (error) {
		return (
			<Box
				sx={{
					height: "50px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					margin: "10px auto",
					backgroundColor: "var(--primary-main)",
					borderRadius: "var(--radius-sm)",
					color: "var(--primary-contrast)",
					fontWeight: "bold",
					fontSize: "1.25rem",
				}}
			>
				{alt}
			</Box>
		);
	}

	return <Logo src={src} alt={alt} onError={() => setError(true)} {...props} />;
};

const MenuDivider = styled(Divider)(({ theme }) => ({
	width: "100%",
	margin: `${theme.spacing(2)} 0`,
	backgroundColor: "var(--divider)",
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
	margin: `${theme.spacing(1)} 0`,
	borderRadius: "var(--radius-sm)", // Using small border radius from design system
	backgroundColor: "var(--background-paper)",
	boxShadow: "var(--shadow-sm)", // Using small shadow from design system
	transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)", // Improved animation curve
	position: "relative",
	overflow: "hidden",
	boxSizing: "border-box",
	"&:hover": {
		backgroundColor: "var(--grey-100)",
		transform: "translateX(4px)",
		boxShadow: "var(--shadow-md)", // Using medium shadow from design system
	},
	"&:after": {
		content: '""',
		position: "absolute",
		bottom: 0,
		left: 0,
		width: 0,
		height: "3px",
		backgroundColor: "var(--primary-main)",
		transition: "width 0.3s ease-in-out",
	},
	"&:hover:after": {
		width: "100%",
	},
}));

const StyledNavLink = styled(NavLink)(({ theme }) => ({
	color: "var(--primary-main)",
	textDecoration: "none",
	width: "100%",
	maxWidth: "100%", // Prevent overflow
	margin: `${theme.spacing(1)} 0`, // Only vertical margins
	padding: `0 ${theme.spacing(2)}`, // Use padding for horizontal spacing instead
	boxSizing: "border-box", // Include padding in width calculation
	display: "block", // Ensure block-level element
	"&.active": {
		"& .MuiListItem-root": {
			backgroundColor: "var(--grey-100)",
			boxShadow: "inset 4px 0 0 var(--primary-main), var(--shadow-md)",
			"&:after": {
				width: "100%",
			},
		},
		"& .MuiListItemIcon-root": {
			color: "var(--primary-dark)",
		},
		"& .MuiListItemText-primary": {
			fontWeight: 700,
		},
	},
}));

const Menu = ({ onNavigate = null }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { userData } = useAuth();
	const { hasERP } = useAccountType();
	const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
	const [sidebarItemSettings, setSidebarItemSettings] = useState({});

	// Handle navigation callback (for closing mobile drawer)
	const handleNavigation = useCallback(() => {
		if (onNavigate) {
			onNavigate();
		}
	}, [onNavigate]);

	// Detect if user is poultry type
	const isPoultry =
		userData && typeof userData.Type === "string"
			? userData.Type.toLowerCase() === "poultry"
			: false;

	// Load sidebar item settings from localStorage
	useEffect(() => {
		try {
			const savedSettings = localStorage.getItem("sidebarItemSettings");
			if (savedSettings) {
				const parsedSettings = JSON.parse(savedSettings);
				// Ensure all values are boolean
				const cleanedSettings = {};
				Object.entries(parsedSettings).forEach(([key, value]) => {
					cleanedSettings[key] = Boolean(value);
				});
				setSidebarItemSettings(cleanedSettings);
			}
		} catch (error) {
			console.error("[Menu][useEffect] - Failed to load sidebar settings from localStorage:", error);
		}
	}, []);

	// Save sidebar item settings to localStorage
	const handleSaveSettings = useCallback((newSettings) => {
		try {
			// Ensure all values are boolean
			const cleanedSettings = {};
			Object.entries(newSettings).forEach(([key, value]) => {
				cleanedSettings[key] = Boolean(value);
			});
			localStorage.setItem("sidebarItemSettings", JSON.stringify(cleanedSettings));
			setSidebarItemSettings(cleanedSettings);
		} catch (error) {
			console.error("[Menu][handleSaveSettings] - Failed to save sidebar settings to localStorage:", error);
		}
	}, []);

	const allMainMenuItems = useMemo(
		() => {
			const allItems = [
				{
					path: "/",
					icon: <DashboardIcon />,
					label: t("dashboard"),
					description: t("dashboard_description"),
				},
				{
					path: "/cockpit",
					icon: <QrCodeScannerIcon />,
					label: t("cockpit") || "Cockpit",
					description: t("cockpit-description") || "Fast animal lookup and field workflows",
				},
				{
					path: "/calf-erp",
					icon: <InventoryIcon />,
					label: t("calf-erp") || "Cattle ERP",
					description: t("calf-erp-description") || "Lifecycle, vaccinations, weights, health and feed records",
				},
				{
					path: "/calf-dashboard",
					icon: <InsightsIcon />,
					label: t("calf-dashboard") || "Calf KPIs",
					description: t("calf-dashboard-description") || "Herd health KPIs and analytics",
				},
				{
					path: "/animals",
					icon: <AnalyticsIcon />,
					label: t("animals"),
					description: t("animals_description"),
				},
				{
					path: "/barn",
					icon: <StoreIcon />,
					label: t("barn"),
					description: t("barn_description"),
				},
				{
					path: "/treatments",
					icon: <VaccinesIcon />,
					label: t("treatment"),
					description: t("treatment_description"),
				},
				{
					path: "/workplaner",
					icon: <ScheduleIcon />,
					label: t("workplaner"),
					description: t("workplaner_description"),
				},
	
	
			];

			// Paths that require ERP/herd management
			const erpPaths = ["/calf-erp", "/calf-dashboard", "/treatments", "/workplaner"];

			return allItems.filter(item => {
				// Filter out ERP-specific items for BioTag-only users
				if (!hasERP && erpPaths.includes(item.path)) return false;
				return true;
			});
		},
		[t, hasERP]
	);

	// Filter main menu items based on settings (default to visible if not set)
	const mainMenuItems = useMemo(
		() => allMainMenuItems.filter(item => sidebarItemSettings[item.path] !== false),
		[allMainMenuItems, sidebarItemSettings]
	);

	const allBottomMenuItems = useMemo(
		() => [
			{
				path: "/archive",
				icon: <ArchiveIcon />,
				label: t("archive"),
				description: t("archive_description"),
			},
			{
				path: "/settings",
				icon: <SettingsIcon />,
				label: t("settings"),
				description: t("settings_description"),
			},
		],
		[t]
	);

	// Filter bottom menu items based on settings (default to visible if not set)
	const bottomMenuItems = useMemo(
		() => allBottomMenuItems.filter(item => sidebarItemSettings[item.path] !== false),
		[allBottomMenuItems, sidebarItemSettings]
	);

	const renderMenuItem = (item) => (
		<StyledNavLink key={item.path} to={item.path} end onClick={handleNavigation}>
			<StyledListItem>
				<ListItemIcon
					sx={{
						color: "var(--primary-main)",
						minWidth: 40,
						transition: "color 0.3s ease",
					}}
				>
					{item.icon}
				</ListItemIcon>
				<ListItemText
					primary={item.label}
					sx={{
						"& .MuiListItemText-primary": {
							color: "var(--primary-main)",
							fontWeight: 500,
							fontSize: "0.95rem",
							transition: "font-weight 0.3s ease",
						},
					}}
				/>
			</StyledListItem>
		</StyledNavLink>
	);

	return (
		<>
			<MenuWrapper>
				<MenuContainer>
					<Box>
						<LogoContainer>
							<LogoWithFallback
								src={isPoultry ? logoPoultry : isBioTagPlus ? logoBD : isFoersterTag ? logoFoerster : logo}
								alt="LOGO"
								onClick={() => navigate("/")}
							/>
							<SettingsIconButton
								onClick={() => setSettingsDialogOpen(true)}
								aria-label={t("sidebar-settings") || "Sidebar Settings"}
								size="small"
							>
								<TuneIcon fontSize="small" />
							</SettingsIconButton>
						</LogoContainer>

						<MenuDivider />
						<List disablePadding>
							{mainMenuItems.map((item) => {
								return renderMenuItem(item);
							})}
						</List>
					</Box>

					<Box>
						<MenuDivider />
						<List disablePadding>{bottomMenuItems.map(renderMenuItem)}</List>
					</Box>
				</MenuContainer>
			</MenuWrapper>

			<SidebarSettingsDialog
				open={settingsDialogOpen}
				onClose={() => setSettingsDialogOpen(false)}
				mainMenuItems={allMainMenuItems}
				bottomMenuItems={allBottomMenuItems}
				onSave={handleSaveSettings}
				currentSettings={sidebarItemSettings}
			/>
		</>
	);
};

export default Menu;
